import {useEffect, useState, useMemo ,useRef} from 'react';
import {Heading,HEADING_TYPES, DatePicker, ChoiceChips} from 'cdk-radial'
import { CommonDropdown } from "../../common/CommonSelector";
import { MultiSelectDropdownMenu } from '../../common/MultiSelectDropdownMenu';
import { useDashboardData } from '../useDashboard';
import {accountingGLUrl,accountingGLCommonUrl} from '../DashboardConfig';
import { APP_ENV } from '../../../constants';
import userProfileStore from '../../core/UserProfileStore';
import useUserSettingStore from '../UserSettingStore';
import {GL_DASHBOARD} from '../useGLDashboard';
import styled from 'styled-components';
import { useTranslation } from "react-i18next";
import moment from 'moment';

interface Option {
    value: string | number;
    label: string;
    obj: any;
}


export const getUserDetail = () => {
  const config = [];
  config.push({
    id: 'group',
    apiConfig: {
      method: 'GET',
      path: '/user/details/with-account-type/A',
      endpoint: accountingGLCommonUrl[APP_ENV as unknown as keyof typeof accountingGLCommonUrl],
    },
  });
  return config;
};


export const getCalendarAndCompany = (storeIds: string[], companyId: string) => {
  const config = [];
  config.push({
    id: 'coa',
    apiConfig: {
      method: 'GET',
      path: '/gl-coa',
      queryParams: {
        stores: storeIds,
        filterRestrictedCOA: false,
      },
      endpoint: accountingGLUrl[APP_ENV as unknown as keyof typeof accountingGLUrl]
    },
  });
  config.push({
    id: 'coa',
    apiConfig: {
      method: 'GET',
      path: '/gl-calendar',
      queryParams: {
        type: 'open',
        companyId: companyId,
      },
      endpoint: accountingGLUrl[APP_ENV as unknown as keyof typeof accountingGLUrl]
    },
  });
  return config;
};



export const getMapping = (accountingGroup: any, glGroup: any) => {
  return (
    accountingGroup?.map((accGroup: any) => {
      const organizationUnits = accGroup.organizationUnits;
      const map = organizationUnits
        ?.map((org: any) =>
          glGroup?.find((gGroup: any) => gGroup.groupId === org)
        )
        ?.filter((o: any) => o != null);
      return {
        ...accGroup,
        glMap: map,
      };
    }) || []
  );
};

export const StyledFieldRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
  @media (max-width: 1100px) {
    display: grid;
    max-width: 70%;
  }
`;

export const StyledDatePickerWrapper = styled.div<{ maxWidth?: string }>`
  max-width: ${(props) => props.maxWidth || '250px'};
  width: 100%;
  position: relative;
  .DayPicker {
    position: absolute;
    right: 0;
    top: 100%;
    margin-top: 8px;
    z-index: 1000;
  }
`;

export const DatePresetChipGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

export const DatePresetChip = styled.button<{ isActive?: boolean }>`
  padding: 8px 16px;
  border: none;
  background: ${(props) => props.isActive ? '#0066CC' : '#E8E8E8'};
  color: ${(props) => props.isActive ? '#FFFFFF' : '#333333'};
  border-radius: 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  box-shadow: ${(props) => props.isActive ? '0 2px 4px rgba(0, 102, 204, 0.2)' : '0 1px 2px rgba(0, 0, 0, 0.1)'};
  
  &:hover:not(:disabled) {
    background: ${(props) => props.isActive ? '#0052A3' : '#D0D0D0'};
    box-shadow: ${(props) => props.isActive ? '0 3px 6px rgba(0, 102, 204, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.15)'};
  }
  
  &:active:not(:disabled) {
    box-shadow: ${(props) => props.isActive ? '0 1px 2px rgba(0, 102, 204, 0.2)' : '0 1px 2px rgba(0, 0, 0, 0.1)'};
  }
  
  &:focus {
    outline: none;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const getGLFilterSetting = (
  allGroup: any,
  currentStoreId: string,
  filter: any
) => {
    if (filter) {
      const storeIds =
        filter.group?.stores?.map((store: any) => store.storeId) || [];
      return {
        group: filter.group,
        company: filter.company,
        accounts: filter.accounts,
        date: filter.date,
        reportDate: filter.reportDate,
        reportDaysAgo: filter.reportDaysAgo,
        storeIds,
      };
    }

  // Default fallback for first-time users
  const defaultGroup = allGroup?.find((agroup: any) =>
    agroup.stores?.find((stores: any) => stores.storeId === currentStoreId)
  );
  const defaultCompany = defaultGroup?.glMap;
  const storeIds =
    defaultGroup?.stores?.map((store: any) => store.storeId) || [];

  return {
    group: defaultGroup,
    company: defaultCompany,
    storeIds,
    accounts: undefined,
    date: undefined,
    reportDate: undefined,
    reportDaysAgo: 0, // Default to "Current Period" for first-time users
  };
};

export const useGLDashboardFilter = ({userSetting, enabled = true}:any) => {

    const { currentStoreId } = userProfileStore();

    const [userDetailConfig, setUserDetailConfig] = useState<any>(null);
    const [coaConfig, setCoaConfig] = useState<any>(null);
    const [filterSetting, setFilterSetting] = useState() as any;

    useEffect(() => {
        if (!enabled) {
            setUserDetailConfig(null);
            setCoaConfig(null);
            setFilterSetting(undefined);
            return;
        }
        setUserDetailConfig(getUserDetail());
    }, [userSetting, enabled]);

    const userDetailData = useDashboardData({ config: userDetailConfig, enabled: !!userDetailConfig,});
    const coaData = useDashboardData({ config: coaConfig, enabled: !!coaConfig  });

    const allGroup = useMemo(() => {
        const groups = userDetailData?.rawData?.[0]?.eisGroups?.groups;
        if (!groups) return [];
        const accountingGroup = groups.filter((g: any) => g.type === 'ACCOUNTING_GROUP');
        const glGroup = groups.filter((g: any) => g.type === 'GL_GROUP');
        return getMapping(accountingGroup, glGroup);
    }, [userDetailData?.rawData]);

    const derivedSettings = useMemo(() => {
        if (!allGroup?.length) return null;
        const userAppliedFilter = userSetting.dashboard?.find((d: any) => d.id === GL_DASHBOARD.DASHBOARD_ID)?.filter;
        const filter = getGLFilterSetting(allGroup, currentStoreId, userAppliedFilter);
        setFilterSetting(filter);
        return filter;
    }, [allGroup, currentStoreId, userSetting]);

    useEffect(() => {
        if (!derivedSettings?.group || !derivedSettings?.storeIds) return;
        // Ensure we update our local state so the UI stays in sync
        const companyId = derivedSettings.company?.[0]?.companyId;
        if (companyId) {
            const stores = Array.isArray(derivedSettings.storeIds) ? derivedSettings.storeIds : [derivedSettings.storeIds];
            setCoaConfig(getCalendarAndCompany(stores, companyId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [derivedSettings?.group?.groupId, derivedSettings?.company?.[0]?.companyId]);

    const defaultAccounts = useMemo(() => {
        const glAccounts = coaData?.rawData?.[0] || [];
        return glAccounts.slice(0, 10)
    }, [coaData]);

    const glDate = useMemo(() => {
        const calendar = coaData?.rawData?.[1]?.find((d: any) => d.monthStatus === 'current');
        if (!calendar) return null;
        return new Date(calendar.postingYear || 0, calendar.postingMonth || 0, 0).toISOString().slice(0, 10);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userSetting,coaData?.rawData?.[1]]);

    return {
        isLoading: enabled && (userDetailData.isLoading || coaData.isLoading || !userDetailData?.rawData || (!!coaConfig && !coaData?.rawData)),
        filterSetting,
        defaultAccounts,
        glDate
    };

}

export const GLFilter = ({ onChange, onValidationError, widget }: any) => {

    const isInitialLoad = useRef(true);
    const isDateModeInitialized = useRef(false);
    const tempDateRef = useRef<any>(null); // Store date temporarily when switching modes
    const prevCompanyIdsRef = useRef<string>("");
    const {t} = useTranslation();

    // Check if this is an Unposted Transaction widget (date filter should be hidden)
    const isUnpostedWidget = widget && (
        widget.id === 'gl-unposted-transaction' || 
        widget.id === 'gl-unposted-error'
    );

    const { userSetting } = useUserSettingStore();
    const { filterSetting, isLoading: filterIsLoading } = useGLDashboardFilter({ userSetting });
    const { currentStoreId } = userProfileStore();

    // 1. Config State
    const [userDetailConfig, setUserDetailConfig] = useState<any>(null);
    const [coaConfig, setCoaConfig] = useState<any>(null);

    // 2. Data Fetching Hooks
    const userDetailData = useDashboardData({
        config: userDetailConfig,
        enabled: !!userDetailConfig,
    });
    const coaData = useDashboardData({ config: coaConfig, enabled: !!coaConfig });

    // 3. Selection State
    const [allGroup, setAllGroup] = useState<any[]>([]);
    const [groupOptions, setGroupOptions] = useState<Option[]>([]);
    const [selectGroup, setSelectGroup] = useState<Option | null>(null);

    const [companyOptions, setCompanyOptions] = useState<Option[]>([]);
    const [selectCompany, setSelectCompany] = useState<Option[]>([]);

    const [accountsOptions, setAccountsOptions] = useState<Option[]>([]);
    const [selectAccounts, setSelectAccounts] = useState<Option[]>([]);

    const [queryDate, setQueryDate] = useState<string>("");
    const [selectedDate, setSelectedDate] = useState<any>(null);
    const [datePreset, setDatePreset] = useState<string>('custom');

    const [isDropdownChange, setIsDropdownChange] = useState(false);

    // Block future dates from tomorrow onwards
    const blockedFutureDates = useMemo(() => [
        {
            startDate: moment().add(1, 'day'),
            endDate: moment('2100-12-31')
        }
    ], []);

    // Validate date selection: require either a valid date OR a preset (not custom)
    useEffect(() => {
        // Check if selected date is in the future
        if (selectedDate && moment(selectedDate).isAfter(moment(), 'day')) {
             onValidationError(true);
        }
        // If no date selected and no preset selected, disable save
        else if (!selectedDate && datePreset === 'custom') {
            onValidationError(true);
        }
        else if (!filterIsLoading) {
            // Only enable if not loading and valid date/preset is selected
            onValidationError(false);
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDate, datePreset, filterIsLoading]);

    // Initialize User Details
    useEffect(() => {
        setUserDetailConfig(getUserDetail());
    }, []);

    // 4. Handle UserDetail & Initial Filter Settings
    useEffect(() => {
        const rawData = userDetailData?.rawData?.[0];
        if (!rawData) return;

        const accountingGroup = rawData?.eisGroups?.groups?.filter((g: any) => g.type === 'ACCOUNTING_GROUP');
        const glGroup = rawData?.eisGroups?.groups?.filter((g: any) => g.type === 'GL_GROUP');
        const groups = getMapping(accountingGroup, glGroup);
        setAllGroup(groups);

        const options = groups?.map((group: any) => ({
            value: group?.name,
            label: group?.name,
            obj: group
        }));
        setGroupOptions(options);

        // Logic for Default Selection (from settings or current store)
        let targetGroupObj = groups?.find((g: any) => g.name === filterSetting?.group?.name);
        
        if (!targetGroupObj) {
            targetGroupObj = groups?.find((agroup: any) =>
                agroup.stores?.find((s: any) => s.storeId === currentStoreId)
            );
        }

        if (targetGroupObj) {
            const foundOption = options.find((o: any) => o.value === targetGroupObj.name);
            setSelectGroup(foundOption || null);
            
            // Setup Companies for this group
            const cOptions = targetGroupObj.glMap?.map((acc: any) => ({
                value: acc?.groupId,
                label: acc?.name,
                obj: acc
            })) || [];
            
            setCompanyOptions(cOptions);
            setSelectCompany(cOptions); // Default to selecting all companies in group

            const storeIds = targetGroupObj.stores?.map((s: any) => s.storeId) || [];
            setCoaConfig(getCalendarAndCompany(storeIds, targetGroupObj.glMap?.[0]?.companyId));
        }
        
    }, [userDetailData?.rawData, filterSetting, currentStoreId]);

    useEffect(() => {
        if (!coaData?.rawData) return;

        const allAccounts = coaData?.rawData?.[0] || [];
        const currentCompanyIds = selectCompany?.map((c: any) => c.obj.companyId).join(",") || "";

        const acctOptions = allAccounts
            .filter((acc: any) => currentCompanyIds.includes(acc.company_id))
            .map((acc: any) => ({
                value: acc?.account_number,
                label: `${acc?.company_id}-${acc?.account_displayName}`,
                obj: acc
            }));

        setAccountsOptions(acctOptions);

        if(!isDropdownChange){
            setSelectAccounts(acctOptions.slice(0, 10));
        }
        
        if (isInitialLoad.current && filterSetting?.accounts?.length > 0) {
            const acctNo = filterSetting.accounts.map((c: any) => c.account_number);
            const savedOptions = acctOptions.filter((acct: any) => acctNo.includes(acct.value));
            
            if (savedOptions.length > 0) {
                setSelectAccounts(savedOptions);
                isInitialLoad.current = false; 
                prevCompanyIdsRef.current = currentCompanyIds; // Sync the ref
            }

        } 
        else if (prevCompanyIdsRef.current !== currentCompanyIds) {
            //setSelectAccounts(acctOptions.slice(0, 10));
            prevCompanyIdsRef.current = currentCompanyIds; // Update the ref
        }

        const glCal = coaData?.rawData[1]?.find((data: any) => data.monthStatus === 'current');
        if (glCal) {
            const dateStr = new Date(glCal.postingYear, glCal.postingMonth, 0).toISOString().slice(0, 10);
            setQueryDate(dateStr);
            
            // Initialize selectedDate from filterSetting (only once)
            if (!isDateModeInitialized.current) {
                if (filterSetting?.reportDate) {
                    // If reportDate is saved, set the date
                    const savedDate = moment(filterSetting.reportDate);
                    setSelectedDate(savedDate);
                    tempDateRef.current = savedDate; // Store in temp ref
                    setDatePreset('custom');
                } else if (filterSetting?.reportDaysAgo !== undefined) {
                    // If reportDaysAgo is saved, set the appropriate preset
                    setSelectedDate(null);
                    // Set the appropriate preset based on days
                    if (filterSetting.reportDaysAgo === 0) {
                        setDatePreset('currentperiod');
                    } else if (filterSetting.reportDaysAgo === 1) {
                        setDatePreset('today');
                    } else if (filterSetting.reportDaysAgo === 7) {
                        setDatePreset('7days');
                    } else if (filterSetting.reportDaysAgo === 14) {
                        setDatePreset('14days');
                    }
                } else {
                    // Default for first-time users: Use Current Period (GL calendar date)
                    setSelectedDate(null);
                    setDatePreset('currentperiod');
                }
                isDateModeInitialized.current = true;
            }
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coaData?.rawData, selectCompany, filterSetting]);

    // Update date preset when filterSetting changes (for modal reopening)
    useEffect(() => {
        if (!filterSetting) return;
        
        const hasReportDate = filterSetting.reportDate && filterSetting.reportDate !== '';
        
        if (hasReportDate) {
            const savedDate = moment(filterSetting.reportDate);
            setSelectedDate(savedDate);
            tempDateRef.current = savedDate;
            setDatePreset('custom');
        } else {
            const daysAgo = Number(filterSetting.reportDaysAgo);
            
            if (!isNaN(daysAgo) && daysAgo >= 0) {
                setSelectedDate(null);
                
                if (daysAgo === 0) {
                    setDatePreset('currentperiod');
                } else if (daysAgo === 1) {
                    setDatePreset('today');
                } else if (daysAgo === 7) {
                    setDatePreset('7days');
                } else if (daysAgo === 14) {
                    setDatePreset('14days');
                } else {
                    setDatePreset('currentperiod');
                }
            } else {
                setSelectedDate(null);
                setDatePreset('currentperiod');
            }
        }
    }, [filterSetting]);

    // Handlers
    const handleSelectGroup = (select: Option) => {
        setSelectGroup(select);
        setIsDropdownChange(true)
        const groupData = allGroup?.find((g: any) => g.name === select.label);
        
        const cOptions = groupData?.glMap?.map((acc: any) => ({
            value: acc?.groupId,
            label: acc?.name,
            obj: acc
        })) || [];

        setCompanyOptions(cOptions);
        setSelectCompany([]); // Clear company and accounts on group change
        setAccountsOptions([]);
        setSelectAccounts([]);

        onValidationError(true)
    };

    const handleSelectCompany = (select: Option[]) => {
        setSelectCompany(select);
        setSelectAccounts([]); // Reset accounts when company changes
        onValidationError(true)
        setIsDropdownChange(true)
        const companyIds = select?.map((s: any) => s.obj?.companyId) || [];
        const storeIds = select?.map((s: any) => s.obj?.organizationUnits) || [];
        
        if (companyIds.length > 0) {
            setCoaConfig(getCalendarAndCompany(storeIds, companyIds[0]));
        }
    };

    const handleSelectAccounts = (select: Option[]) => {
     
        const isInvalid = select.length === 0;
        onValidationError(isInvalid);
        setSelectAccounts(select);
        // Validation is handled by the comprehensive validation effect

        // Determine reportDate and reportDaysAgo based on selection
        let reportDate = null;
        let reportDaysAgo = 0;
        
        if (selectedDate) {
            // Date selected from picker
            reportDate = selectedDate.format('YYYY-MM-DD');
            reportDaysAgo = 0;
        } else if (datePreset !== 'custom') {
            // Preset button selected
            reportDate = null;
            if (datePreset === 'currentperiod') {
                reportDaysAgo = 0;
            } else if (datePreset === 'today') {
                reportDaysAgo = 1;
            } else if (datePreset === '7days') {
                reportDaysAgo = 7;
            } else if (datePreset === '14days') {
                reportDaysAgo = 14;
            }
        } else if (selectedDate) {
            // Fallback: has a date but mode unclear
            reportDate = selectedDate.format('YYYY-MM-DD');
            reportDaysAgo = 0;
        }
        if (!isInvalid) {
        onChange({
            group: selectGroup?.obj,
            company: selectCompany?.map((c: any) => c.obj),
            accounts: select?.map((a: any) => a.obj),
            date: queryDate,
            reportDate,
            reportDaysAgo,
        });
    }
    };

    const handleDateChange = (date: any) => {
        setSelectedDate(date);
        tempDateRef.current = date; // Store in temp ref whenever date changes
        setDatePreset('custom'); // Reset preset when date is selected
        
        onChange({
            group: selectGroup?.obj,
            company: selectCompany?.map((c: any) => c.obj),
            accounts: selectAccounts?.map((a: any) => a.obj),
            reportDate: date ? date.format('YYYY-MM-DD') : queryDate,
            reportDaysAgo: 0,
        });
    };

    const handleDatePreset = (preset: string) => {
        setDatePreset(preset);
        setSelectedDate(null); // Reset the date picker when preset is selected
        tempDateRef.current = null; // Clear temp ref
        
        let daysAgo;
        
        switch(preset) {
            case 'currentperiod':
                daysAgo = 0;
                break;
            case 'today':
                daysAgo = 1;
                break;
            case '7days':
                daysAgo = 7;
                break;
            case '14days':
                daysAgo = 14;
                break;
            default:
                return;
        }
        
        onChange({
            group: selectGroup?.obj,
            company: selectCompany?.map((c: any) => c.obj),
            accounts: selectAccounts?.map((a: any) => a.obj),
            reportDate: null,
            reportDaysAgo: daysAgo,
        });
    };



    return (
        <div>
            {filterIsLoading && <div>Loading ...</div>}
            {!filterIsLoading && 
            <>

            {!isUnpostedWidget && (
                <>
                    <Heading level={3} type={HEADING_TYPES.SUBTITLE_3} style={{ marginBottom: '10px' }}>
                        Date Settings (Daily Balance Date)
                    </Heading>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap', marginTop: '10px' }}>
                    <DatePresetChipGroup>
                        <ChoiceChips
                            dataTestId="gl-date-preset-chips"
                            onClick={(value: string) => handleDatePreset(value)}
                            id="gl-date-preset-chips"
                            name="datePreset"
                            options={[
                                { label: t('dashboard.datePresets.currentPeriod') as string, value: 'currentperiod' },
                                { label: t('dashboard.datePresets.today') as string, value: 'today' },
                                { label: t('dashboard.datePresets.7daysAgo') as string, value: '7days' },
                                { label: t('dashboard.datePresets.14daysAgo') as string, value: '14days' }
                            ]}
                            value={datePreset === 'custom' ? '' : datePreset}
                            isDisabled={filterIsLoading}
                        />
                    </DatePresetChipGroup>
                        
                    
                        <StyledDatePickerWrapper maxWidth="250px" style={{ marginBottom: '20px', marginLeft: '10px' }}>
                            <DatePicker
                                dataTestId="gl-filter-date-picker"
                                date={selectedDate}
                                dateFormat="MM/DD/YYYY"
                                dateInputProps={{
                                    enableCustomValidation: false,
                                    errorMessage: '',
                                    hasError: false,
                                    isReadOnly: false,
                                    isRequired: false,
                                    isDisabled: filterIsLoading,
                                    size: 'standard',
                                    onError: () => {},
                                }}
                                allowDaysInPast={true}
                                blockedDateRanges={blockedFutureDates}
                                id="gl-filter-date-picker"
                                label={(t('dashboard.settings.selectDate', { defaultValue: 'Select Date' }) as any)}
                                maximumYear={2100}
                                minimumYear={1900}
                                name="gl-filter-date-picker"
                                numberOfMonths={1}
                                onDateChange={handleDateChange}
                                shortcutChipsPosition="none"
                            />
                        </StyledDatePickerWrapper>
                    </div>
                </>
            )}
            <Heading level={3} type={HEADING_TYPES.SUBTITLE_3}>
                Filter Settings (Unposted & Account Balance)
            </Heading>
            <div style={{ marginTop: '5px' }}>
                <StyledFieldRow>
                    <div style={{ flex: 1 }}>
                        <CommonDropdown
                            options={groupOptions}
                            label={t('dashboard.settings.selGroup') as any}
                            selectedOption={selectGroup as any}
                            isRequired
                            onSelect={handleSelectGroup} 
                        />
                    </div>
                </StyledFieldRow>

                <div style={{ flex: 1, marginTop: '10px' }}>
                    <MultiSelectDropdownMenu
                        name="company"
                        options={companyOptions}
                        hasError={selectCompany.length === 0}
                        onSelect={handleSelectCompany}
                        value={selectCompany}
                        isRequired
                        label={t('dashboard.settings.selCompany') as any}
                        placeholder={t('dashboard.settings.selCompany') as any}
                    />
                </div>

                <div style={{ marginTop: '10px', marginBottom:'10px' }}>
                    <MultiSelectDropdownMenu
                        name="accounts"
                        options={accountsOptions}
                        hasError={selectAccounts.length === 0}
                        onSelect={handleSelectAccounts}
                        maxSelection={10}
                        value={selectAccounts}
                        isRequired
                        label={t('dashboard.settings.selAccounts') as any}
                        placeholder={t('dashboard.settings.selAccounts') as any}
                    />
                    ({t('dashboard.settings.maxOf10accts')}) 
                </div>

              
            </div>
            
            </>}

        </div>
    );
};
